import Grid from '@material-ui/core/Grid';
import React, { HTMLAttributes } from 'react';
import {
  MvpTableRow,
  TableRow,
} from 'src/components/PortalList/PortalList.styles';
import DocumentTitle from 'src/components/PortalList/PortalSigningCaseDetails/DocumentTitle';
import { IDocumentResponse, ISigningCase } from 'src/models';
import { GridStyled } from 'src/styles/layout';
import { DocumentStatus } from './DocumentStatus';
import { GridIcon, GridRow, MvpAppGridRow } from './DocumentsListItem.styles';
import { FileIcon, TableCell } from 'src/components';
import { getDocumentTitleHash } from './getDocumentTitleHash';
import { isSigningCaseSigned } from 'src/utils/SigningCase';
import { useUrlParameterCheck } from 'src/utils/useUrlParameterCheck';
import { MvpFileIcon } from 'src/components/Icons/FileIcon';
import { DocumentTagStyled } from 'src/components/Tag/Tag.styles';
import { getTextFromDocumentStatus } from 'src/utils/getTextFromDocumentStatus';

interface Props extends HTMLAttributes<HTMLTableRowElement> {
  document: IDocumentResponse;
  status: ISigningCase['status'];
  downloadLink: string;
}

export const DocumentsListItem = ({
  document,
  status,
  downloadLink,
  ...rest
}: Props) => {
  const isFromMvpApp = useUrlParameterCheck('/app');

  return isFromMvpApp ? (
    <MvpTableRow
      role="row"
      {...rest}
      data-testid="data-testid"
      key={document.id}
    >
      <TableCell role="cell">
        <MvpAppGridRow
          container
          wrap="nowrap"
          isRead={document.isRead}
          status={status}
        >
          <GridIcon
            container
            item
            xs
            padding={16}
            direction="column"
            alignItems="center"
          >
            <Grid
              item
              style={{
                background: '#d3d3d37a',
                padding: '10px',
                borderRadius: '15px',
              }}
            >
              <MvpFileIcon type={document.mimeType} />
            </Grid>
          </GridIcon>
          <GridStyled container item padding={12}>
            <Grid item={true} md={10}>
              <DocumentTitle
                name={document.name}
                hash={getDocumentTitleHash(document) || ''}
                isBold={!document.isRead}
              />
            </Grid>
            <GridStyled
              container
              item
              md={2}
              justifyContent="flex-end"
              screenUp="tabletSmall"
              alignItems="center"
            />
            {status !== 'Updating' ? (
              <DocumentTagStyled
                status={status}
                isFromMvpApp={isFromMvpApp}
                document={document}
              >
                {getTextFromDocumentStatus(status, document)}
              </DocumentTagStyled>
            ) : null}
          </GridStyled>
          <GridStyled
            item
            padding={4}
            style={{
              display: 'flex',
              alignItems: 'center',
              margin: 'auto 16px',
            }}
          >
            <DocumentStatus
              isRead={document.isRead}
              showText={false}
              canDownload={canDownload(status, document.isRead)}
              downloadLink={downloadLink}
            />
          </GridStyled>
        </MvpAppGridRow>
      </TableCell>
    </MvpTableRow>
  ) : (
    <TableRow role="row" {...rest} data-testid="data-testid" key={document.id}>
      <TableCell role="cell">
        <GridRow
          container
          wrap="nowrap"
          isRead={document.isRead}
          status={status}
        >
          <GridIcon
            container
            item
            xs
            padding={12}
            direction="column"
            alignItems="center"
          >
            <Grid item>
              <FileIcon type={document.mimeType} />
            </Grid>
          </GridIcon>
          <GridStyled container item padding={12}>
            <Grid item={true} md={10}>
              <DocumentTitle
                name={document.name}
                hash={getDocumentTitleHash(document) || ''}
                isBold={!document.isRead}
              />
            </Grid>
            <GridStyled
              container
              item
              md={2}
              justifyContent="flex-end"
              screenUp="tabletSmall"
              alignItems="center"
            >
              <Grid item={true}>
                <DocumentStatus
                  isRead={document.isRead}
                  showText={true}
                  canDownload={canDownload(status, document.isRead)}
                  downloadLink={downloadLink}
                />
              </Grid>
            </GridStyled>
          </GridStyled>
          <GridStyled
            item
            padding={4}
            style={{ display: 'flex', alignItems: 'center' }}
            screenDown="tabletSmall"
          >
            <DocumentStatus
              isRead={document.isRead}
              showText={false}
              canDownload={canDownload(status, document.isRead)}
              downloadLink={downloadLink}
            />
          </GridStyled>
        </GridRow>
      </TableCell>
    </TableRow>
  );
};

const canDownload = (
  status: ISigningCase['status'],
  isDocumentRead: IDocumentResponse['isRead']
) => isSigningCaseSigned(status) && isDocumentRead;
